/**
 * Drone D1 #320 — "Request live eyes" affordance (COP-side, gov-facing).
 *
 * Complements the existing "Recommend Drone Inspection" button (DroneCoverageBody):
 * given an area of interest (the selected incident, else this provider's location) and
 * the capability needs, it triggers the STATELESS matching query (`POST /api/drone-match`)
 * and renders the fit-ranked operator options. Picking an option CREATES a 'requested'
 * drone mission targeting that operator (Drone D1 #319) — the operator then accepts /
 * declines / flies it from their portal, and the requestor tracks + bills it in the
 * "Your drone missions" panel below. (Dispatch notify + ETA/price negotiation is #321.)
 * The match itself logs an audit action like the recommend button (reusing
 * `recommend_drone_inspection`, tagged `via: request_live_eyes`); the create is scribed
 * server-side, so the client does not double-log it.
 */
import { useState } from 'react';
import {
  DRONE_PLATFORM_CATEGORIES,
  DRONE_REQUEST_URGENCIES,
  DRONE_SENSOR_TYPES,
  eyesOnEnduranceCheck,
  formatCad,
  pricingTierLabel,
  type DroneMatchCandidate,
  type DroneMatchResponse,
  type DronePlatformCategory,
  type DroneReadiness,
  type DroneRequestUrgency,
  type DroneSensorType,
  type OperatorPricingTier,
  type RateCard,
} from '@tsr/shared-types';
import { createDroneMission, matchDrones } from './api';
import { DRONE_AMBIENT_COLOR } from './droneCoverage';
import { logAction } from './lib/operatorActions';
import { showToast } from './lib/toast';

/** Default eyes-on watch window (minutes, #345). A 2-hour default gives a meaningful price
 *  estimate out of the box. Exported so the coverage panel can seed + share the SAME value with
 *  the §3B.6 endurance honesty check (one source of truth — no drift between the two surfaces). */
export const DEFAULT_EYES_ON_MINUTES = 120;

/** Tailwind chip classes per pricing tier (#344) — on-palette, never red/green for status. */
const TIER_BADGE_CLASS: Record<OperatorPricingTier, string> = {
  gov: 'bg-emerald-500/20 text-emerald-200',
  'at-cost': 'bg-cyan-500/20 text-cyan-200',
  market: 'bg-amber-500/20 text-amber-200',
};

/** The §3B.6 readiness band per candidate (Phase 4c) — 'committed' = busy on a mission. */
const READINESS_BADGE: Record<DroneReadiness, { label: string; cls: string }> = {
  available: { label: 'Available', cls: 'bg-emerald-500/20 text-emerald-200' },
  committed: { label: 'Committed', cls: 'bg-amber-500/20 text-amber-200' },
  maintenance: { label: 'Maintenance', cls: 'bg-slate-500/20 text-slate-300' },
  unavailable: { label: 'Offline', cls: 'bg-slate-600/40 text-slate-300' },
};

/** The estimate line for a candidate: gov ⇒ "No charge"; rate unset ⇒ "Rate not set"; else est. */
function estimateLabel(c: DroneMatchCandidate): string {
  if (c.pricing_tier === 'gov') return 'No charge';
  if (c.estimate_cad == null) return 'Rate not set';
  return `${formatCad(c.estimate_cad)} est.`;
}

/** Compact rate-card breakdown ("$750 call-out · $320/hr · $4.50/km"), or null when none set. */
function rateSummary(card: RateCard | null): string | null {
  if (!card) return null;
  const parts: string[] = [];
  if (card.mobilization_fee_cad != null) parts.push(`$${card.mobilization_fee_cad} call-out`);
  if (card.hourly_rate_cad != null) parts.push(`$${card.hourly_rate_cad}/hr`);
  if (card.per_km_cad != null) parts.push(`$${card.per_km_cad}/km`);
  return parts.length ? parts.join(' · ') : null;
}

const SENSOR_LABEL: Record<DroneSensorType, string> = {
  eo: 'EO (daylight)',
  ir: 'IR / thermal',
  multispectral: 'Multispectral',
  lidar: 'LiDAR',
  sar: 'SAR',
  gas: 'Gas / chem',
  'comms-relay': 'Comms relay',
};

const PLATFORM_LABEL: Record<DronePlatformCategory, string> = {
  multirotor: 'Multirotor',
  'fixed-wing': 'Fixed-wing',
  vtol: 'VTOL',
  male: 'MALE',
};

/** Phase 4e — the 3-level requested-urgency vocab (mirrors agency_tasks.urgency, §I.7 Q4).
 *  Default 'routine'; the operator triages the incoming request by this level (§I.2 ☎4). */
const URGENCY_OPTION_LABEL: Record<DroneRequestUrgency, string> = {
  routine: 'Routine',
  urgent: 'Urgent',
  immediate: 'Immediate',
};

function OptionCard({
  c,
  onSelect,
  busy,
  eyesOnMinutes,
}: {
  c: DroneMatchCandidate;
  onSelect: (c: DroneMatchCandidate) => void;
  busy: boolean;
  /** The requested eyes-on duration (watch window, minutes) — labels the estimate the price is for. */
  eyesOnMinutes: number;
}): JSX.Element {
  // §3B.6 / §G honesty (#345): if the requested watch window outruns THIS drone's flight endurance,
  // say so — the operator may still accept relay/sortie coverage, so it warns, it does not block.
  const endurance = eyesOnEnduranceCheck(eyesOnMinutes, c.flight_endurance_min ?? null);
  return (
    <li className="rounded border border-slate-700/60 bg-slate-800/40 px-3 py-2" data-testid="drone-match-option">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{c.asset_name}</p>
          <p className="text-[11px] text-slate-400 truncate">{c.operator_name}</p>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-0.5">
          <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-200">
            fit {Math.round(c.fit_score * 100)}%
          </span>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded ${TIER_BADGE_CLASS[c.pricing_tier]}`}
            data-testid="drone-tier-badge"
            data-tier={c.pricing_tier}
          >
            {pricingTierLabel(c.pricing_tier)}
          </span>
        </div>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-200">
          {c.platform_category ? PLATFORM_LABEL[c.platform_category] : 'Platform —'}
        </span>
        {c.sensors.map((s) => (
          <span
            key={s}
            className={`px-1.5 py-0.5 rounded ${
              c.matched_sensors.includes(s)
                ? 'bg-emerald-500/20 text-emerald-200'
                : 'bg-slate-700/60 text-slate-300'
            }`}
          >
            {SENSOR_LABEL[s]}
          </span>
        ))}
      </div>

      <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
        <dt className="text-slate-400">Distance</dt>
        <dd className="font-mono text-right">
          {c.reachability.distance_km} km {c.bearing}
        </dd>
        <dt className="text-slate-400">Range slack</dt>
        <dd className="font-mono text-right">{c.reachability.slack_km} km</dd>
        <dt className="text-slate-400">ETA</dt>
        <dd className="font-mono text-right" data-testid="drone-match-eta">
          {c.reachability.eta_minutes == null ? '—' : `~${c.reachability.eta_minutes} min`}
        </dd>
        <dt className="text-slate-400">Readiness</dt>
        <dd className="text-right">
          {c.readiness ? (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded ${READINESS_BADGE[c.readiness].cls}`}
              data-testid="drone-match-readiness"
              data-readiness={c.readiness}
            >
              {READINESS_BADGE[c.readiness].label}
            </span>
          ) : (
            <span className="font-mono">—</span>
          )}
        </dd>
        <dt className="text-slate-400">Authorization</dt>
        <dd className="font-mono text-right">{c.auth_level_summary ?? '—'}</dd>
        <dt className="text-slate-400">
          Estimate{eyesOnMinutes > 0 ? ` (${eyesOnMinutes} min eyes-on)` : ''}
        </dt>
        <dd className="font-mono text-right" data-testid="drone-estimate">
          {estimateLabel(c)}
        </dd>
      </dl>
      {rateSummary(c.rate_card) && (
        <p className="mt-1 text-[10px] text-slate-500">Rate card: {rateSummary(c.rate_card)}</p>
      )}

      {endurance.exceeds && (
        <p
          className="mt-1.5 text-[11px] text-amber-200/90 leading-relaxed"
          data-testid="drone-endurance-warning"
        >
          ⚠ Exceeds this drone’s {endurance.enduranceMin}-min endurance — the full {eyesOnMinutes}-min
          watch needs {endurance.sortiesNeeded} sorties with turnaround gaps, or a relay of aircraft
          for continuous coverage.
        </p>
      )}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => onSelect(c)}
          className="rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50 px-2.5 py-1 text-[12px] font-medium"
        >
          {busy ? 'Requesting…' : 'Select'}
        </button>
        {c.illustrative && (
          <span className="text-[10px] text-amber-300/90">Illustrative — pending partnership</span>
        )}
      </div>
    </li>
  );
}

export function RequestLiveEyes({
  ao,
  aoLabel,
  incidentId = null,
  onMissionCreated,
  eyesOnMinutes,
  onEyesOnMinutesChange,
}: {
  /** The area of interest to match against (incident point, else provider location). */
  ao: { lat: number; lon: number } | null;
  /** Human label for what the AO is, shown under the heading. */
  aoLabel: string;
  /** The linked incident, when the AO is an incident (carried onto the created mission). */
  incidentId?: string | null;
  /** Called after a mission is created so the requestor's missions panel can refresh. */
  onMissionCreated?: () => void;
  /** Optional CONTROLLED eyes-on duration (minutes, #345 / 4c.1). When provided the input is
   *  controlled by the parent so a sibling surface (the §3B.6 endurance check in DroneCoverageBody)
   *  can read the SAME live value. Omit (the default for the 7 standalone callers) ⇒ the component
   *  owns the value internally, seeded to {@link DEFAULT_EYES_ON_MINUTES}, exactly as before. */
  eyesOnMinutes?: number;
  /** Notified on every eyes-on change. Required to make `eyesOnMinutes` controlled; harmless to
   *  pass alone (the parent just mirrors the value). */
  onEyesOnMinutesChange?: (minutes: number) => void;
}): JSX.Element {
  const [required, setRequired] = useState<Set<DroneSensorType>>(new Set(['eo', 'ir']));
  const [platform, setPlatform] = useState<DronePlatformCategory | ''>('');
  // Eyes-on window the requestor wants (minutes, #345). Feeds the per-candidate estimate + the
  // 4c.1 endurance warning; a 2-hour default gives a meaningful estimate out of the box. 0 / blank
  // ⇒ omitted (no duration). Controllable: when the parent passes `eyesOnMinutes` the value comes
  // from the prop (single source of truth shared with the §3B.6 check); otherwise it is internal.
  const [internalEyesOn, setInternalEyesOn] = useState<number>(
    eyesOnMinutes ?? DEFAULT_EYES_ON_MINUTES,
  );
  const onStationMinutes = eyesOnMinutes ?? internalEyesOn;
  const setOnStationMinutes = (next: number): void => {
    if (eyesOnMinutes == null) setInternalEyesOn(next);
    onEyesOnMinutesChange?.(next);
  };
  // Phase 4e — the requestor's declared urgency, carried onto the created mission so the operator
  // triages it in-COP (§I.2 ☎4). Default routine; independent of the eyes-on watch window.
  const [urgency, setUrgency] = useState<DroneRequestUrgency>('routine');
  const [busy, setBusy] = useState(false);
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [result, setResult] = useState<DroneMatchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const select = async (c: DroneMatchCandidate): Promise<void> => {
    setSelectingId(c.drone_asset_id);
    try {
      await createDroneMission({
        operator_agency_id: c.operator_agency_id,
        drone_asset_id: c.drone_asset_id,
        incident_id: incidentId,
        ao,
        required_sensors: DRONE_SENSOR_TYPES.filter((s) => required.has(s)),
        platform_category: platform === '' ? null : platform,
        requested_on_station_minutes: onStationMinutes > 0 ? onStationMinutes : null,
        requested_urgency: urgency,
      });
      // The server scribes drone_mission_requested — no client double-log.
      showToast(`Mission requested from ${c.operator_name} — awaiting their response.`, {
        variant: 'success',
      });
      onMissionCreated?.();
    } catch {
      showToast('Could not create the mission — try again.', { variant: 'error' });
    } finally {
      setSelectingId(null);
    }
  };

  const toggleSensor = (s: DroneSensorType): void => {
    setRequired((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const request = async (): Promise<void> => {
    if (!ao) return;
    const required_sensors = DRONE_SENSOR_TYPES.filter((s) => required.has(s));
    const platform_category = platform === '' ? undefined : platform;
    setBusy(true);
    setError(null);
    // Audit like the recommend button — recommend/forward only, no tasking write.
    logAction('recommend_drone_inspection', {
      via: 'request_live_eyes',
      ao,
      required_sensors,
      platform_category: platform_category ?? null,
    });
    try {
      const res = await matchDrones({
        ao,
        required_sensors,
        platform_category,
        requested_on_station_minutes: onStationMinutes > 0 ? onStationMinutes : undefined,
      });
      setResult(res);
    } catch {
      setError('Matching failed — try again.');
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border-t border-slate-800 pt-3" data-testid="request-live-eyes">
      <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Request live eyes</h3>
      <p className="text-[11px] text-slate-500 mb-2">
        Match drone operators that can reach <span className="text-slate-300">{aoLabel}</span> and
        cover the selected sensors. Selecting an operator sends a mission request — they
        accept, decline, or fly it from their portal.
      </p>

      <div className="mb-2">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Required sensors</p>
        <div className="flex flex-wrap gap-1.5">
          {DRONE_SENSOR_TYPES.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={required.has(s)}
              onClick={() => toggleSensor(s)}
              className={`text-[11px] px-1.5 py-0.5 rounded border ${
                required.has(s)
                  ? 'border-emerald-500/50 bg-emerald-500/20 text-emerald-200'
                  : 'border-slate-600/50 bg-slate-700/40 text-slate-300'
              }`}
            >
              {SENSOR_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-2 flex items-center gap-2">
        <label htmlFor="rle-platform" className="text-[10px] uppercase tracking-wider text-slate-500">
          Platform
        </label>
        <select
          id="rle-platform"
          value={platform}
          onChange={(e) => setPlatform(e.target.value as DronePlatformCategory | '')}
          className="text-[12px] rounded bg-slate-700/60 border border-slate-600/50 px-1.5 py-0.5"
        >
          <option value="">Any</option>
          {DRONE_PLATFORM_CATEGORIES.map((p) => (
            <option key={p} value={p}>
              {PLATFORM_LABEL[p]}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-2 flex items-center gap-2">
        <label
          htmlFor="rle-on-station"
          className="text-[10px] uppercase tracking-wider text-slate-500"
        >
          Eyes-on duration
        </label>
        <input
          id="rle-on-station"
          type="number"
          min={0}
          max={100000}
          step={15}
          value={onStationMinutes}
          onChange={(e) => setOnStationMinutes(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
          className="w-20 text-[12px] rounded bg-slate-700/60 border border-slate-600/50 px-1.5 py-0.5"
          data-testid="rle-on-station"
        />
        <span className="text-[11px] text-slate-500">min (drives the price estimate)</span>
      </div>

      <div className="mb-2 flex items-center gap-2">
        <label htmlFor="rle-urgency" className="text-[10px] uppercase tracking-wider text-slate-500">
          Urgency
        </label>
        <select
          id="rle-urgency"
          value={urgency}
          onChange={(e) => setUrgency(e.target.value as DroneRequestUrgency)}
          className="text-[12px] rounded bg-slate-700/60 border border-slate-600/50 px-1.5 py-0.5"
          data-testid="rle-urgency"
        >
          {DRONE_REQUEST_URGENCIES.map((u) => (
            <option key={u} value={u}>
              {URGENCY_OPTION_LABEL[u]}
            </option>
          ))}
        </select>
        <span className="text-[11px] text-slate-500">how the operator triages this request</span>
      </div>

      <button
        type="button"
        disabled={busy || !ao}
        onClick={() => void request()}
        className="w-full rounded px-3 py-2 text-sm font-medium disabled:opacity-50"
        style={{ backgroundColor: DRONE_AMBIENT_COLOR, color: '#1e1b2e' }}
      >
        {busy ? 'Matching…' : 'Request live eyes'}
      </button>
      {!ao && (
        <p className="text-[10px] text-slate-500 mt-1.5">
          Select an incident to set the area of interest.
        </p>
      )}

      {error && <p className="text-[11px] text-rose-300 mt-2">{error}</p>}

      {result && (
        <div className="mt-3">
          {result.candidates.length === 0 ? (
            <div className="rounded bg-slate-700/30 border border-slate-600/40 px-3 py-2">
              <p className="text-sm text-slate-300">No reachable operator matches</p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                No active operator can reach this area with the selected sensors. Widen the
                sensor set or platform.
              </p>
            </div>
          ) : (
            <>
              <p className="text-[11px] text-slate-400 mb-1.5">
                {result.count} fit-ranked option{result.count === 1 ? '' : 's'}
              </p>
              <ul className="space-y-2">
                {result.candidates.map((c) => (
                  <OptionCard
                    key={c.drone_asset_id}
                    c={c}
                    onSelect={(cand) => void select(cand)}
                    busy={selectingId === c.drone_asset_id}
                    eyesOnMinutes={onStationMinutes}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
